import * as React from 'react'
import { cn } from '@klear/ui/lib/utils'
import type { LucideIcon } from 'lucide-react'

export interface InputProps extends Omit<React.ComponentProps<'input'>, 'size'> {
  inlayIcon?: LucideIcon
  inlayIconPosition?: 'left' | 'right'
  size?: 'default' | 'sm' | 'xs' | 'lg'
  containerClassName?: string
  onEnter?: () => void
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type,
      inlayIcon: InlayIcon,
      inlayIconPosition = 'left',
      size = 'default',
      containerClassName,
      onEnter,
      onKeyDown,
      ...props
    },
    ref
  ) => {
    const sizeClasses =
      size === 'sm'
        ? 'h-8 px-3 py-1 text-sm'
        : size === 'xs'
          ? 'h-7 px-2.5 py-1 text-xs'
          : size === 'lg'
            ? 'h-10 px-4 py-2 text-base'
            : 'h-9 px-3 py-1 text-sm'

    return (
      <div className={cn('relative', containerClassName)}>
        {InlayIcon && inlayIconPosition === 'left' && (
          <InlayIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        )}
        <input
          type={type}
          className={cn(
            'flex w-full rounded-md border border-input bg-background shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
            sizeClasses,
            InlayIcon && inlayIconPosition === 'left' && 'pl-9',
            InlayIcon && inlayIconPosition === 'right' && 'pr-9',
            className
          )}
          ref={ref}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              onEnter?.()
            }
            onKeyDown?.(e)
          }}
          {...props}
        />
        {InlayIcon && inlayIconPosition === 'right' && (
          <InlayIcon className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        )}
      </div>
    )
  }
)
Input.displayName = 'Input'

export { Input }
